import {
  Box,
  IconButton,
  Popover,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useState } from "react";

const thSx = {
  background: "#04343a",
  color: "#fff",
  fontWeight: 700,
  fontSize: "13px",
  whiteSpace: "nowrap",
  borderRight: "1px solid rgba(255,255,255,0.15)",
  "&:last-child": { borderRight: "none" },
};

const tdSx = {
  borderRight: "1px solid #e2e8f0",
  "&:last-child": { borderRight: "none" },
};

const SECTION_COLORS = {
  FUTURES: { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8" },
  OPTIONS: { bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d" },
  CASH:    { bg: "#fefce8", border: "#fde68a", text: "#a16207" },
};

const defaultColor = { bg: "#f8fafc", border: "#e2e8f0", text: "#475569" };

const ProfitCardsTable = ({ cards, loading, onEdit, onToggleActive, onUploadImage }) => {
  const [hoverAnchor, setHoverAnchor] = useState(null);
  const [hoverCard, setHoverCard] = useState(null);

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
        overflow: "hidden",
      }}
    >
      <TableContainer>
        <Table sx={{ borderCollapse: "collapse" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...thSx, width: 48 }}>SNo.</TableCell>
              <TableCell sx={thSx}>Title</TableCell>
              <TableCell sx={{ ...thSx, width: 110 }}>Date</TableCell>
              <TableCell sx={thSx}>Sections</TableCell>
              <TableCell sx={{ ...thSx, width: 90 }}>Status</TableCell>
              <TableCell sx={{ ...thSx, width: 70, textAlign: "center" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 8 }}>
                  <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : !Array.isArray(cards) || cards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 8 }}>
                  <Typography sx={{ color: "#94a3b8", fontSize: "13px" }}>
                    No profit cards yet. Create one!
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              cards.map((card, idx) => (
                <TableRow
                  key={card.id}
                  sx={{
                    "&:hover": { background: "#f8fafc" },
                    transition: "background 0.15s",
                    borderBottom: "1px solid #e2e8f0",
                    verticalAlign: "middle",
                  }}
                >
                  {/* SNo */}
                  <TableCell sx={{ ...tdSx, color: "#94a3b8", fontSize: "13px", fontWeight: 500 }}>
                    {idx + 1}
                  </TableCell>

                  {/* Title */}
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontWeight: 700, fontSize: "13px", color: "#04343a" }}>
                      {card.title}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "#94a3b8", mt: 0.3 }}>
                      ID: {card.id}
                    </Typography>
                  </TableCell>

                  {/* Date */}
                  <TableCell sx={tdSx}>
                    <Typography sx={{ fontSize: "13px", color: "#475569", fontWeight: 500 }}>
                      {card.date}
                    </Typography>
                  </TableCell>

                  {/* Sections */}
                  <TableCell sx={{ ...tdSx, py: 1.5 }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {(card.sections || [])
                        .sort((a, b) => a.order - b.order)
                        .map((sec) => {
                          const color = SECTION_COLORS[sec.label] || defaultColor;
                          return (
                            <Box
                              key={sec.id}
                              sx={{
                                border: `1px solid ${color.border}`,
                                background: color.bg,
                                borderRadius: "8px",
                                px: 1,
                                py: 0.5,
                                minWidth: "80px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "10px",
                                  fontWeight: 700,
                                  color: color.text,
                                  letterSpacing: "0.5px",
                                  mb: 0.5,
                                }}
                              >
                                {sec.label}
                              </Typography>
                              {(sec.metrics || []).map((m) => (
                                <Box key={m.id}>
                                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}>
                                    {m.primary_value}
                                  </Typography>
                                  {m.suffix && sec.label !== "CASH" && (
                                    <Typography sx={{ fontSize: "11px", color: "#64748b" }}>
                                      {m.suffix}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          );
                        })}
                    </Box>
                  </TableCell>

                  {/* Status */}
                  <TableCell sx={tdSx}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Switch
                        checked={!!card.is_active}
                        onChange={() => onToggleActive(card)}
                        size="small"
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: "#016551" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#016551" },
                        }}
                      />
                      {/* <Typography sx={{ fontSize: "11px", color: card.is_active ? "#016551" : "#94a3b8", fontWeight: 600 }}>
                        {card.is_active ? "Active" : "Off"}
                      </Typography> */}
                    </Box>
                  </TableCell>

                  {/* Actions */}
                  <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEdit(card)}
                          sx={{ color: "#016551", background: "#f0faf7", "&:hover": { background: "#016551", color: "#fff" }, transition: "all 0.2s" }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Upload Image">
                        <IconButton size="small" onClick={() => onUploadImage(card)}
                          sx={{ color: "#0369a1", background: "#eff6ff", "&:hover": { background: "#0369a1", color: "#fff" }, transition: "all 0.2s" }}>
                          <FileUploadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Preview Image">
                        <IconButton
                          size="small"
                          onMouseEnter={(e) => { setHoverAnchor(e.currentTarget); setHoverCard(card); }}
                          onMouseLeave={() => { setHoverAnchor(null); setHoverCard(null); }}
                          sx={{ color: "#7c3aed", background: "#f5f3ff", "&:hover": { background: "#7c3aed", color: "#fff" }, transition: "all 0.2s" }}
                        >
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Hover Image Popover */}
      <Popover
        open={!!hoverAnchor}
        anchorEl={hoverAnchor}
        onClose={() => { setHoverAnchor(null); setHoverCard(null); }}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        disableRestoreFocus
        sx={{ pointerEvents: "none" }}
        slotProps={{
          paper: { sx: { borderRadius: "10px", overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", width: 260 } },
        }}
      >
        <Box sx={{ background: "#04343a", px: 2, py: 1 }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "12px", noWrap: true }}>
            {hoverCard?.title}
          </Typography>
        </Box>
        {hoverCard?.image ? (
          <Box
            component="img"
            src={hoverCard.image}
            alt={hoverCard?.title}
            sx={{ width: "100%", maxHeight: 220, objectFit: "contain", display: "block", background: "#0f172a" }}
          />
        ) : (
          <Box sx={{ py: 4, textAlign: "center", background: "#0f172a" }}>
            <VisibilityIcon sx={{ fontSize: 36, color: "#334155", mb: 0.5 }} />
            <Typography sx={{ color: "#475569", fontSize: "12px" }}>No image uploaded yet</Typography>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default ProfitCardsTable;
